// Utility functions for date/time formatting

/**
 * Convert Unix timestamp (seconds) to a JavaScript Date object.
 *
 * @param timestamp - The Unix timestamp in seconds to convert.
 * @returns A new Date object representing the given timestamp.
 */
export const unixToDate = (timestamp: number): Date => {
  return new Date(timestamp * 1000);
};

/**
 * Convert a JavaScript Date object to a Unix timestamp (seconds).
 *
 * @param date - The Date object to convert.
 * @returns The Unix timestamp representation in seconds.
 */
export const dateToUnix = (date: Date): number => {
  return Math.floor(date.getTime() / 1000);
};

/**
 * Format a Unix timestamp (in seconds) to a localized string based on the given format.
 * Includes support for relative time formatting (e.g., "Just now", "2h ago").
 *
 * @param timestamp - Unix timestamp in seconds.
 * @param format - The desired format: 'full', 'short', 'time', 'date', or 'relative'.
 * @returns A formatted string representation of the date and time.
 */
export const formatUnixTimestamp = (
  timestamp: number,
  format: 'full' | 'short' | 'time' | 'date' | 'relative' = 'full',
): string => {
  const date = unixToDate(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  switch (format) {
    case 'full':
      // e.g., "Jan 9, 2026 at 3:45 PM"
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });

    case 'short':
      // e.g., "1/9/26, 3:45 PM"
      return date.toLocaleString(undefined, {
        year: '2-digit',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });

    case 'time':
      // e.g., "3:45 PM"
      return date.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });

    case 'date':
      // e.g., "Jan 9, 2026"
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });

    case 'relative':
      // e.g., "just now", "5 minutes ago", "2 hours ago"
      if (diffSecs < 60) {
        return 'just now';
      } else if (diffMins < 60) {
        return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
      } else if (diffHours < 24) {
        return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
      } else if (diffDays < 7) {
        return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
      } else {
        return formatUnixTimestamp(timestamp, 'date');
      }

    default:
      return date.toLocaleString();
  }
};

/**
 * Get current Unix timestamp
 */
export const getCurrentUnixTimestamp = (): number => {
  return Math.floor(Date.now() / 1000);
};

/**
 * Formats a message timestamp specifically for the chat UI, providing a concise
 * representation based on whether the message was sent today, yesterday, or earlier.
 *
 * @param timestamp - Unix timestamp in seconds of the message.
 * @returns A formatted string (e.g., "Today at 2:30 PM", "Yesterday at 9:15 AM", "05/12/2023 10:00 AM").
 */
export const formatMessageTimestamp = (timestamp: number): string => {
  const date = unixToDate(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  if (isToday) {
    return formatUnixTimestamp(timestamp, 'time');
  } else if (isYesterday) {
    return `Yesterday at ${formatUnixTimestamp(timestamp, 'time')}`;
  } else if (now.getTime() - date.getTime() < 7 * 24 * 60 * 60 * 1000) {
    // Within last week - show day name
    return date.toLocaleDateString(undefined, {
      weekday: 'long',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } else {
    return formatUnixTimestamp(timestamp, 'full');
  }
};

/**
 * Format last seen timestamp
 */
export const formatLastSeen = (timestamp: number): string => {
  const now = getCurrentUnixTimestamp();
  const diffSecs = now - timestamp;

  if (diffSecs < 60) {
    return 'Online';
  } else if (diffSecs < 300) {
    // 5 minutes
    return 'Active recently';
  } else {
    return `Last seen ${formatUnixTimestamp(timestamp, 'relative')}`;
  }
};
/**
 * Finds and replaces `<t:timestamp:style>` markers within a message content string
 * with the actual formatted time.
 *
 * Supports standard timestamp formats:
 * - `t`: Time
 * - `d`: Date
 * - `s`: Short
 * - `f`: Full
 * - `r`: Relative Time
 *
 * @param content - The raw message content string containing potential timestamp markers.
 * @returns The message string with all valid timestamp markers replaced by localized strings.
 */
export const parseTimestampsInMessage = (content: string): string => {
  const timestampRegex = /<t:(\d+)(?::([fsrdt]))?>/g;

  return content.replace(timestampRegex, (match, timestamp, format) => {
    const ts = parseInt(timestamp, 10);
    if (isNaN(ts)) return match;

    let formatType: 'full' | 'short' | 'time' | 'date' | 'relative' = 'full';
    switch (format) {
      case 's':
        formatType = 'short';
        break;
      case 't':
        formatType = 'time';
        break;
      case 'd':
        formatType = 'date';
        break;
      case 'r':
        formatType = 'relative';
        break;
      case 'f':
      default:
        formatType = 'full';
        break;
    }

    return formatUnixTimestamp(ts, formatType);
  });
};

/**
 * Create a timestamp marker for inserting into messages
 */
export const createTimestampMarker = (
  timestamp: number,
  format: 'f' | 's' | 't' | 'd' | 'r' = 'f',
): string => {
  return `<t:${timestamp}:${format}>`;
};
